import { clamp01, easeOut, hash2, type Scene } from "./noise.js";
import type { ArtPalette } from "./palette.js";
import {
  type Grid,
  type Mask,
  type Outline,
  paintPicture,
  type Picture,
} from "./picture.js";
import { drawRipple, type Ripple } from "./ripple.js";

/** A picture arriving cell by cell from a point, or turning into the next one. */
type Motion =
  | {
      readonly kind: "arrive";
      readonly start: number;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly kind: "turn";
      readonly start: number;
      readonly x: number;
      readonly y: number;
      readonly from: Picture;
    };

/**
 * The pixel art's canvas (DESIGN.md §9, "Pixel art").
 *
 * A picture arrives from its bright point, turns cell by cell into the next
 * one, and — where the art is interactive — the cells under the pointer lift
 * and lean away from it, then settle. It draws only while something moves:
 * a still picture costs nothing per frame.
 */
export class PixelField {
  private grid: Grid | null = null;
  private picture: Picture | null = null;
  private motion: Motion | null = null;
  private ripple: Ripple | null = null;
  private frameId = 0;
  private last = 0;

  private readonly columns: number;
  private readonly cellSize: number | ((width: number, height: number) => number) | null;
  private readonly still: boolean;
  private readonly outline: Outline | null;
  private readonly shape: ((grid: Grid) => Mask) | null;
  private mask: Mask | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ctx: CanvasRenderingContext2D,
    private palette: ArtPalette,
    options: {
      readonly columns: number;
      /** A fixed cell, one worked out from the box each time it resizes, or none for `columns`. */
      readonly cell: number | ((width: number, height: number) => number) | null;
      readonly still: boolean;
      /** A bare picture's outline; none for a picture with its background. */
      readonly outline: Outline | null;
      /** A shape to cut the picture to, worked out again for each grid; none for no shape. */
      readonly mask?: (grid: Grid) => Mask;
    },
  ) {
    this.columns = options.columns;
    this.cellSize = options.cell;
    this.still = options.still;
    this.outline = options.outline;
    this.shape = options.mask ?? null;
  }

  /** Fits the canvas to its box. Call again when the box changes size. */
  resize(width: number, height: number): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cell =
      typeof this.cellSize === "function"
        ? this.cellSize(width, height)
        : (this.cellSize ?? Math.max(4, width / this.columns));
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = `${String(width)}px`;
    this.canvas.style.height = `${String(height)}px`;
    const cols = Math.ceil(width / cell);
    const rows = Math.ceil(height / cell);
    this.grid = { width, height, dpr, cell, cols, rows };
    this.mask = this.shape === null ? null : this.shape(this.grid);
    this.ripple = this.still
      ? null
      : { energy: new Float32Array(cols * rows), angle: new Float32Array(cols * rows) };
    this.picture = null;
  }

  /**
   * Shows a scene, in this palette or a new one. The first arrives from its
   * own bright point; any later one turns over, cell by cell, from the middle.
   */
  show(scene: Scene, palette: ArtPalette = this.palette): void {
    const grid = this.grid;
    if (grid === null) return;
    this.palette = palette;
    const from = this.picture;
    this.picture = paintPicture(grid, this.palette, scene, this.mask ?? this.outline);
    if (this.still) {
      this.motion = null;
      this.paint(performance.now());
      return;
    }
    const start = performance.now();
    // A bare picture arrives from its anchor, which is where its light is.
    const anchor = this.outline ?? { x: scene.cx, y: scene.cy };
    this.motion =
      from === null
        ? { kind: "arrive", start, x: grid.width * anchor.x, y: grid.height * anchor.y }
        : { kind: "turn", start, x: grid.width / 2, y: grid.height / 2, from };
    this.kick();
  }

  /** The pointer passed through (x, y); `strength` grows with how fast it moved. */
  touch(x: number, y: number, strength: number): void {
    const grid = this.grid;
    const ripple = this.ripple;
    if (grid === null || ripple === null) return;
    const reach = grid.cell * 5.2;
    const x0 = Math.max(0, Math.floor((x - reach) / grid.cell));
    const x1 = Math.min(grid.cols - 1, Math.ceil((x + reach) / grid.cell));
    const y0 = Math.max(0, Math.floor((y - reach) / grid.cell));
    const y1 = Math.min(grid.rows - 1, Math.ceil((y + reach) / grid.cell));
    for (let row = y0; row <= y1; row++) {
      for (let col = x0; col <= x1; col++) {
        const cx = (col + 0.5) * grid.cell;
        const cy = (row + 0.5) * grid.cell;
        const distance = Math.hypot(cx - x, cy - y);
        if (distance >= reach) continue;
        const lift = Math.pow(1 - distance / reach, 1.15) * strength;
        const i = row * grid.cols + col;
        if (lift > (ripple.energy[i] ?? 0)) {
          ripple.energy[i] = lift;
          ripple.angle[i] = Math.atan2(cy - y, cx - x);
        }
      }
    }
    this.kick();
  }

  /** The cell size, so a pointer trail can be sampled finely enough. */
  get cell(): number {
    return this.grid?.cell ?? 8;
  }

  stop(): void {
    if (this.frameId !== 0) cancelAnimationFrame(this.frameId);
    this.frameId = 0;
  }

  private kick(): void {
    if (this.frameId !== 0) return;
    this.last = 0;
    this.frameId = requestAnimationFrame(this.frame);
  }

  private readonly frame = (now: number): void => {
    const dt = this.last === 0 ? 0.016 : Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const moving = this.paint(now);
    const grid = this.grid;
    const picture = this.picture;
    const rippling =
      grid !== null && picture !== null && this.ripple !== null
        ? drawRipple(
            this.ctx,
            { grid, picture, palette: this.palette, ripple: this.ripple },
            dt,
          )
        : false;
    this.frameId = moving || rippling ? requestAnimationFrame(this.frame) : 0;
  };

  /** Draws the picture, or the motion bringing it in. True while it moves. */
  private paint(now: number): boolean {
    const grid = this.grid;
    const picture = this.picture;
    if (grid === null || picture === null) return false;
    this.ctx.setTransform(grid.dpr, 0, 0, grid.dpr, 0, 0);
    const motion = this.motion;
    if (motion === null) {
      // Cleared first: a bare picture has gaps a lifted cell's last frame would show through.
      this.ctx.clearRect(0, 0, grid.width, grid.height);
      this.ctx.drawImage(picture.canvas, 0, 0, grid.width, grid.height);
      return false;
    }
    const moving =
      motion.kind === "arrive"
        ? this.arrive(grid, picture, motion, now)
        : this.turn(grid, picture, motion, now);
    if (!moving) this.motion = null;
    return moving;
  }

  private arrive(
    grid: Grid,
    picture: Picture,
    motion: Extract<Motion, { kind: "arrive" }>,
    now: number,
  ): boolean {
    const ctx = this.ctx;
    this.blank(0, 0, grid.width, grid.height);
    const elapsed = now - motion.start;
    const far = Math.hypot(grid.width, grid.height) * 0.62;
    let moving = false;
    for (let y = 0; y < grid.rows; y++) {
      for (let x = 0; x < grid.cols; x++) {
        const cx = (x + 0.5) * grid.cell;
        const cy = (y + 0.5) * grid.cell;
        const delay =
          (Math.hypot(cx - motion.x, cy - motion.y) / far) * 560 + hash2(x, y, 3) * 160;
        const p = clamp01((elapsed - delay) / 320);
        if (p < 1) moving = true;
        if (p <= 0) continue;
        const size = grid.cell * easeOut(p);
        const d = grid.dpr;
        ctx.drawImage(
          picture.canvas,
          x * grid.cell * d,
          y * grid.cell * d,
          grid.cell * d,
          grid.cell * d,
          cx - size / 2,
          cy - size / 2,
          size,
          size,
        );
      }
    }
    return moving;
  }

  private turn(
    grid: Grid,
    picture: Picture,
    motion: Extract<Motion, { kind: "turn" }>,
    now: number,
  ): boolean {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, grid.width, grid.height);
    ctx.drawImage(picture.canvas, 0, 0, grid.width, grid.height);
    const elapsed = now - motion.start;
    const far = Math.hypot(grid.width, grid.height);
    const d = grid.dpr;
    let moving = false;
    for (let y = 0; y < grid.rows; y++) {
      for (let x = 0; x < grid.cols; x++) {
        const cy = (y + 0.5) * grid.cell;
        const distance = Math.hypot((x + 0.5) * grid.cell - motion.x, cy - motion.y);
        const p = clamp01(
          (elapsed - ((distance / far) * 520 + hash2(x, y, 5) * 110)) / 240,
        );
        if (p >= 1) continue;
        moving = true;
        // Each cell flips over: the old face shrinks to a line, the new one opens.
        const face = p < 0.5 ? motion.from : picture;
        const open = p < 0.5 ? 1 - p * 2 : (p - 0.5) * 2;
        this.blank(x * grid.cell, y * grid.cell, grid.cell + 0.6, grid.cell + 0.6);
        ctx.drawImage(
          face.canvas,
          x * grid.cell * d,
          y * grid.cell * d,
          grid.cell * d,
          grid.cell * d,
          x * grid.cell,
          cy - (grid.cell * open) / 2,
          grid.cell + 0.6,
          grid.cell * open,
        );
      }
    }
    return moving;
  }

  /** The background over an area, or nothing at all on a bare picture. */
  private blank(x: number, y: number, width: number, height: number): void {
    if (this.outline !== null || this.mask !== null) {
      this.ctx.clearRect(x, y, width, height);
      return;
    }
    this.ctx.fillStyle = this.palette.bg;
    this.ctx.fillRect(x, y, width, height);
  }
}
